/**
 * @fileoverview Écran d'authentification backoffice mocké.
 *
 * Représente le flux contractuel en deux étapes : identifiants puis MFA TOTP.
 * Aucun appel backend n'est effectué dans cette tranche UI.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  FormField,
  Input,
  PasswordInput,
  QrCode,
  ReasonTextarea,
  TotpInput,
  WarningNotice,
  useCopyToClipboard,
} from '@gnpc/ui';
import { Check, Copy, Mail } from 'lucide-react';

type LoginStep = 'credentials' | 'mfa' | 'enrollment' | 'recovery';

/**
 * Codes de secours mockés de l'enrollment — 10 codes déterministes au format
 * XXXX-XXXX, comme le jeu complet généré par le backend à l'activation du MFA.
 */
const ENROLLMENT_BACKUP_CODES = [
  'G7K9-P2LM',
  'AH42-Q9SD',
  'N8P3-Z6VT',
  'M2CX-8BA1',
  'T5RW-K3JN',
  'B9DF-L7QU',
  'X4HS-W2EM',
  'C6VY-R8PT',
  'J3NA-D5KZ',
  'Q8UB-F4GW',
];

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const [step, setStep] = useState<LoginStep>('credentials');
  const [email, setEmail] = useState('aicha.diomande@quipux.ci');
  const [password, setPassword] = useState('MotdepasseDemo2026');
  const [totp, setTotp] = useState('');
  /**
   * Copie groupée des codes de secours (un code par ligne) — même hook DS
   * que `<CopyButton>` pour bénéficier du feedback `copied` transitoire.
   */
  const { copy: copyBackupCodes, copied: backupCodesCopied } = useCopyToClipboard();

  const canSubmitCredentials = email.includes('@') && password.length >= 12;
  const canSubmitMfa = totp.length === 6;

  return (
    <div className="console-login-bg bg-quipux-navy-950 grid min-h-dvh place-items-center px-4 py-10">
      {/* `<main>` : la page de login vit hors `ConsoleLayout` et n'avait
        * aucun landmark principal (axe `landmark-one-main` / `region`). */}
      <main className="relative z-10 w-full max-w-5xl">
        <div className="grid overflow-hidden rounded-lg border border-white/10 bg-white lg:grid-cols-[0.9fr_1.1fr]">
          <section className="bg-quipux-navy-950 p-8 text-white md:p-10">
            <img
              src={`${import.meta.env.BASE_URL}brand/logo_quipux_blanc.png`}
              alt="Quipux Afrique"
              className="h-11 w-auto"
            />
            <div className="mt-12 space-y-3">
              <p className="text-quipux-orange-400 text-sm font-medium">Console agents</p>
              <h1 className="text-3xl font-semibold tracking-tight">Connexion backoffice GNPC</h1>
              <p className="max-w-md text-sm leading-6 text-white/70">
                Accès interne pour suivre les paiements, traiter les anomalies d'enregistrement
                Quipux et consulter les journaux selon les capacités de l'agent.
              </p>
            </div>
          </section>

          <Card className="rounded-none border-0">
            <CardContent className="p-6 md:p-8">
              {step === 'credentials' ? (
                <form
                  className="grid gap-5"
                  onSubmit={(event) => {
                    event.preventDefault();
                    setStep('mfa');
                  }}
                >
                  <div className="space-y-1">
                    <h2 className="text-quipux-navy-950 text-2xl font-semibold">Connexion</h2>
                    <p className="text-muted-foreground text-sm">
                      Veuillez saisir vos identifiants.
                    </p>
                  </div>
                  <FormField label="Email professionnel" required>
                    <Input
                      type="email"
                      autoComplete="email"
                      leftIcon={<Mail />}
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                    />
                  </FormField>
                  <FormField label="Mot de passe" required hint="12 caractères minimum.">
                    <PasswordInput
                      autoComplete="current-password"
                      minLength={12}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                  </FormField>
                  <Button type="submit" size="lg" disabled={!canSubmitCredentials}>
                    Continuer
                  </Button>
                  <Button type="button" variant="link" onClick={() => setStep('recovery')}>
                    Mot de passe oublié ?
                  </Button>
                </form>
              ) : null}

              {step === 'mfa' ? (
                <form
                  className="grid gap-5"
                  onSubmit={(event) => {
                    event.preventDefault();
                    navigate('/');
                  }}
                >
                  <div className="space-y-1">
                    <h2 className="text-quipux-navy-950 text-xl font-semibold">Vérification MFA</h2>
                    <p className="text-muted-foreground text-sm">
                      Saisissez le code à 6 chiffres de votre application d'authentification.
                    </p>
                  </div>
                  <FormField label="Code TOTP" required>
                    <TotpInput value={totp} onChange={setTotp} />
                  </FormField>
                  <Button type="submit" size="lg" disabled={!canSubmitMfa}>
                    Ouvrir la console
                  </Button>
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" onClick={() => setStep('credentials')}>
                      Modifier l'email
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setStep('enrollment')}>
                      Configurer une application MFA
                    </Button>
                  </div>
                </form>
              ) : null}

              {step === 'enrollment' ? (
                <div className="grid gap-5">
                  <div className="space-y-1">
                    <h2 className="text-quipux-navy-950 text-xl font-semibold">Configuration MFA</h2>
                    <p className="text-muted-foreground text-sm">
                      Scannez le QR code dans votre application d'authentification, confirmez un premier code, puis conservez les codes de secours.
                    </p>
                  </div>
                  <div className="flex justify-center rounded-lg border border-dashed p-5">
                    <QrCode
                      value="otpauth://totp/GNPC:aicha.diomande@quipux.ci?secret=JBSWY3DPEHPK3PXP&issuer=GNPC"
                      label="Scanner avec Google Authenticator"
                    />
                  </div>
                  {/* Même règle que la page compte (AccountMfaPage) : les
                    * codes ne sont jamais réaffichés après l'enrôlement. */}
                  <WarningNotice title="Affichés une seule fois">
                    Ces codes ne seront affichés qu'une seule fois. Enregistrez-les dans un
                    emplacement sûr avant de continuer.
                  </WarningNotice>
                  <div className="grid gap-3 rounded-lg border p-4">
                    <div className="grid grid-cols-2 gap-2">
                      {ENROLLMENT_BACKUP_CODES.map((code) => (
                        <code key={code} className="text-quipux-navy-950 font-mono text-sm">
                          {code}
                        </code>
                      ))}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void copyBackupCodes(ENROLLMENT_BACKUP_CODES.join('\n'))}
                    >
                      {backupCodesCopied ? (
                        <Check className="size-4" aria-hidden="true" />
                      ) : (
                        <Copy className="size-4" aria-hidden="true" />
                      )}
                      {backupCodesCopied ? 'Codes copiés' : 'Copier les 10 codes'}
                    </Button>
                  </div>
                  <Button type="button" onClick={() => setStep('mfa')}>
                    J'ai enregistré mes codes
                  </Button>
                </div>
              ) : null}

              {step === 'recovery' ? (
                <div className="grid gap-5">
                  <div className="space-y-1">
                    <h2 className="text-quipux-navy-950 text-xl font-semibold">Réinitialisation</h2>
                    <p className="text-muted-foreground text-sm">
                      L'interface affiche le même message que l'adresse existe ou non.
                    </p>
                  </div>
                  <FormField label="Email professionnel" required>
                    <Input type="email" leftIcon={<Mail />} value={email} onChange={(e) => setEmail(e.target.value)} />
                  </FormField>
                  <ReasonTextarea
                    value="Demande de récupération initiée par l'agent."
                    readOnly
                    aria-label="Contexte de récupération"
                  />
                  <Button type="button" onClick={() => setStep('credentials')}>
                    Envoyer les instructions
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
